// Get environment variables from window.ENV (set at runtime) or process.env (development)
const env = process.env.NODE_ENV === 'development' ? process.env : (window.ENV || {});

// Helper function to parse boolean values
const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return false;
};

// Helper function to validate URL
const validateUrl = (url) => {
  if (!url) {
    const errorMessage = 'No API URL provided in environment variables.';
    console.error(errorMessage);
    
    // In development, use default
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:3000';
    }
    
    throw new Error(errorMessage);
  }

  // If URL doesn't start with http/https, prepend https:// (prefer secure by default)
  const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`;
  
  try {
    new URL(urlWithProtocol);
    console.log('Using API URL:', urlWithProtocol);
    return urlWithProtocol;
  } catch (e) {
    const errorMessage = `Invalid API URL provided: ${url}. Error: ${e.message}`;
    console.error(errorMessage);
    
    // In development, use default
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:3000';
    }
    
    throw new Error(errorMessage);
  }
};

// Helper function to validate and parse environment variables
const validateEnv = () => {
  // Log environment configuration for debugging
  console.log('Environment Configuration:', {
    NODE_ENV: process.env.NODE_ENV,
    API_URL: env.REACT_APP_API_URL,
    REPORTS_SERVER_URL: env.REACT_APP_REPORTS_SERVER_URL,
    isEmbedded: env.REACT_APP_DEPLOY_EMBEDDED,
    authEnabled: env.REACT_APP_AUTH_ENABLED,
    showDataView: env.REACT_APP_SHOW_DATA_VIEW,
    showDataAnalysis: env.REACT_APP_SHOW_DATA_ANALYSIS,
    showDataAugmentation: env.REACT_APP_SHOW_DATA_AUGMENTATION
  });
};

// Validate environment variables on load
validateEnv();

// Helper function to get reports server URL (defaults to localhost:3002 if not set)
const getReportsServerUrl = () => {
  // If REACT_APP_REPORTS_SERVER_URL is explicitly set, use it
  if (env.REACT_APP_REPORTS_SERVER_URL) {
    return validateUrl(env.REACT_APP_REPORTS_SERVER_URL);
  }
  
  // Default to localhost:3002 (reports server default port)
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3002';
  }
  
  // In production, try to use API URL if available, otherwise default to localhost:3002
  if (env.REACT_APP_API_URL) {
    // If API URL is set, try to derive reports server URL from it
    // For now, just default to localhost:3002
    return 'http://localhost:3002';
  }
  
  // Final fallback
  return 'http://localhost:3002';
};

export const config = {
  // API Configuration
  apiUrl: validateUrl(env.REACT_APP_API_URL),
  reportsServerUrl: getReportsServerUrl(),
  
  // Feature Flags
  isEmbedded: parseBoolean(env.REACT_APP_DEPLOY_EMBEDDED),
  authEnabled: parseBoolean(env.REACT_APP_AUTH_ENABLED),
  showDataView: parseBoolean(env.REACT_APP_SHOW_DATA_VIEW),
  showDataAnalysis: parseBoolean(env.REACT_APP_SHOW_DATA_ANALYSIS),
  showDataAugmentation: parseBoolean(env.REACT_APP_SHOW_DATA_AUGMENTATION ?? 'true'),
  
  // Application Settings
  environment: process.env.NODE_ENV
};

// Export raw environment variables for debugging
export const rawEnv = env; 